import React from 'react';
import styles from '../css/modules/ContentPage.module.css';
import s from '../css/modules/Landing.module.css';
import SEO from '../components/SEO';
import JsonLd from './JsonLd';
import Breadcrumbs from './Breadcrumbs';
import { FIRMA, ADRES_PELNY, DOJAZD, MAPS_EMBED, MAPS_LINK, MAPS_NAV } from '../data/firma';
import { BUSINESS_SCHEMA, breadcrumbSchema } from '../data/schema';

/*
 * Kontakt i dojazd (spec sekcja 11).
 *
 * Wezel firmy jest ten sam co na stronie glownej (@id "#business") - wczesniej
 * ta strona opisywala DRUGI podmiot, z wlasnym @id i innymi godzinami.
 */

const PATH = '/kontakt/';

const CRUMBS = [
  { name: 'Strona główna', path: '/' },
  { name: 'Kontakt', path: PATH },
];

const Kontakt = () => {
  const whatsappLink = `https://wa.me/${FIRMA.telefonE164.replace('+', '')}`;
  const staticKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const staticMapUrl = staticKey
    ? `https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(ADRES_PELNY)}&zoom=16&size=800x400&scale=2&maptype=roadmap&markers=color:red|${encodeURIComponent(ADRES_PELNY)}&key=${staticKey}`
    : null;

  return (
    <div className={styles.page}>
      <SEO
        title="Kontakt i dojazd | Sensorini Olsztyn"
        description="Kontakt z gabinetem Sensorini w Olsztynie. Telefon, e-mail, adres, mapa, informacje o dojeździe i umawianiu wizyt."
        pathname={PATH}
        type="website"
      />
      <JsonLd data={[BUSINESS_SCHEMA, breadcrumbSchema(CRUMBS)]} />

      <header className={styles.hero}>
        <span className={`${styles.blob} ${styles.blobA}`} aria-hidden="true" />
        <span className={`${styles.blob} ${styles.blobB}`} aria-hidden="true" />
        <div className={styles.heroInner}>
          <span className={styles.eyebrow}>Kontakt • Olsztyn</span>
          <h1 className={styles.h1}>Kontakt i dojazd do gabinetu Sensorini</h1>
          <p className={styles.lead}>
            Zadzwoń albo napisz - odezwę się i ustalimy termin. Poniżej znajdziesz adres, mapę,
            informacje o dojeździe, parkingu i wejściu do budynku.
          </p>
        </div>
      </header>

      <Breadcrumbs items={CRUMBS} />

      <div className={`${styles.wrap} ${styles.wrapWide}`}>
        <div className={styles.split}>
          <div className={styles.card}>
            <h2 className={styles.name} style={{ marginTop: 0, color: 'var(--deep)' }}>
              {FIRMA.nazwa}
            </h2>

            <address style={{ fontStyle: 'normal', margin: '0 0 1rem', lineHeight: 1.8 }}>
              {FIRMA.ulica}
              <br />
              {FIRMA.kod} {FIRMA.miasto}
              <br />
              tel. <a href={`tel:${FIRMA.telefonE164}`}>{FIRMA.telefon}</a>
              <br />
              <a href={`mailto:${FIRMA.email}`}>{FIRMA.email}</a>
            </address>

            <p className={styles.muted}>
              <strong>Godziny przyjęć:</strong> {FIRMA.godzinyTekst}
            </p>

            <div className={styles.contactRow}>
              <a className={styles.contactLink} href={`tel:${FIRMA.telefonE164}`}>
                ☎ Zadzwoń: {FIRMA.telefon}
              </a>
              <a className={styles.contactLink} href={`mailto:${FIRMA.email}`}>
                ✉ Napisz wiadomość
              </a>
              <a className={styles.contactLink} href={whatsappLink} target="_blank" rel="noreferrer">
                🟢 WhatsApp
              </a>
            </div>
            <div style={{ marginTop: '0.9rem', display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
              <a className={styles.btnOutline} href={MAPS_NAV} target="_blank" rel="noreferrer">
                Wyznacz trasę
              </a>
              <a className={styles.btnOutline} href={MAPS_LINK} target="_blank" rel="noreferrer">
                Otwórz w Google Maps
              </a>
            </div>
          </div>

          <div className={styles.mapBox} aria-label={`Mapa dojazdu: ${ADRES_PELNY}`}>
            {staticMapUrl ? (
              <a href={MAPS_LINK} target="_blank" rel="noreferrer">
                <img
                  src={staticMapUrl}
                  alt={`Gabinet Sensorini - dojazd, ${ADRES_PELNY}`}
                  className={styles.mapImage}
                  loading="lazy"
                  width={800}
                  height={400}
                />
              </a>
            ) : (
              <iframe
                title={`Mapa dojazdu do gabinetu Sensorini, ${ADRES_PELNY}`}
                src={MAPS_EMBED}
                className={styles.mapFrame}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            )}
          </div>
        </div>

        <section className={styles.section} style={{ marginTop: '2.5rem' }}>
          <h2>Jak trafić do gabinetu?</h2>
          <ul className={styles.list}>
            <li>{DOJAZD.dzielnica}</li>
            <li>{DOJAZD.komunikacja}</li>
            <li>{DOJAZD.parking}</li>
            <li>{DOJAZD.wejscie}</li>
            <li>{DOJAZD.dostepnosc}</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>Jak umówić wizytę?</h2>
          <p>
            Najszybciej telefonicznie pod numerem{' '}
            <a href={`tel:${FIRMA.telefonE164}`}>{FIRMA.telefon}</a>. Możesz też napisać na{' '}
            <a href={`mailto:${FIRMA.email}`}>{FIRMA.email}</a> albo wypełnić{' '}
            <a href="/kwestionariusz-sensomotoryczny/">kwestionariusz sensomotoryczny online</a> -
            wtedy odezwę się do Ciebie sama, a na wywiad przyjdziesz z gotowym opisem trudności
            dziecka.
          </p>
          <p>
            Zanim się umówisz, warto zajrzeć na stronę{' '}
            <a href="/diagnoza-sensoryczna-olsztyn/">diagnozy integracji sensorycznej w Olsztynie</a>{' '}
            - opisuję tam przebieg badania, a w <a href="/cennik/">cenniku</a> znajdziesz koszty i
            zasady odwoływania wizyt.
          </p>
        </section>

        <p className={s.disclaimer}>
          Dane kontaktowe podane na tej stronie są jedynymi aktualnymi danymi gabinetu.
        </p>
      </div>

      <section className={styles.ctaBand} id="final-contact">
        <div className={styles.ctaInner}>
          <h2>Umów diagnozę integracji sensorycznej w Olsztynie</h2>
          <p>Wypełnij krótki kwestionariusz online lub zadzwoń - odezwę się i ustalimy termin.</p>
          <div className={styles.ctaBtns}>
            <a
              href="/diagnoza-sensoryczna-olsztyn/"
              className={styles.btnPrimary}
              data-cta="booking"
              data-cta-location="final"
            >
              Umów diagnozę
            </a>
            <a href={`tel:${FIRMA.telefonE164}`} className={styles.btnGhost}>
              Zadzwoń: {FIRMA.telefon}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Kontakt;
